import { Fragment, useContext, useEffect, useState } from 'react';
import { FaDownload, FaPlus, FaSearch, FaTrash } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DeleteMassiveFileAPI, DownloadMassiveAPI, GetFilesAPI } from '../../../API/FilesAPI';
import { GetSubPathNameAPI, GetSubPathsAPI } from '../../../API/PathAPI';
import CreatePathComponent from '../../../Components/PathManager/CreatePathComponent';
import FileTableComponent from '../../../Components/PathManager/FileTableComponent';
import ModalSearchFileComponent from '../../../Components/PathManager/ModalSearchFileComponent';
import PathTableComponent from '../../../Components/PathManager/PathTableComponent';
import UploadFileComponent from '../../../Components/PathManager/UploadFileComponent';
import CardHeader from '../../../Components/Shared/CardHeader';
import Loader from '../../../Components/Shared/Loader';
import ModalDeleteMassive from '../../../Components/Shared/ModalDeleteMassive';
import { AuthContext } from '../../../Contexts/AuthContext';
import { CompanyContext } from '../../../Contexts/CompanyContext';
import useCan from '../../../Hooks/useCan';
import useLoading from '../../../Hooks/useLoading';
import useModal from '../../../Hooks/useModal';
import { IResponseProps } from '../../../Interfaces/IResponseProps';
import { FileModel } from '../../../Models/FileModel';
import { PathModel } from '../../../Models/PathModel';
import { PathsInFileManager } from '../../../Static/PathsInFileManager';
import './styles.css';

const PathManager = () => {
    const canNotDoNothing = useCan({ rolesCan: ["Internal"] });
    const { loading, toggleLoading } = useLoading();
    const { pathId, pathMain } = useParams();
    const { showModal, toggleShowModal } = useModal();
    const { showModal: showCreateModal, toggleShowModal: toggleShowCreateModal } = useModal();
    const { showModal: showSearchFileModal, toggleShowModal: toggleShowSearchFileModal } = useModal();
    const { userEmail } = useContext(AuthContext);
    const { company } = useContext(CompanyContext);
    const [pathName, setPathName] = useState<string>("");
    const [filesHasChanged, setFilesHasChanged] = useState<boolean>(false);
    const [hasSomeCheck, setHasSomeCheck] = useState<boolean>(false);
    const [total, setTotal] = useState<number>(0);
    const [files, setFiles] = useState<FileModel[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [deleteFiles, setDeleteFiles] = useState<boolean>(false);
    const [totalPath, setTotalPath] = useState<number>(0);
    const [paths, setPaths] = useState<PathModel[]>([]);
    const [searchTextPath, setSearchTextPath] = useState<string>("");
    const [pathsHasChanged, setPathsHasChanged] = useState<boolean>(false);

    const [checkedState, setCheckedState] = useState(
        new Array(files.length).fill(false)
    );

    const createNewPath = () => {
        toggleShowCreateModal();
    }

    const searchFile = () => {
        toggleShowSearchFileModal();
    }

    const buttons = [
        {
            name: "Nova Subpasta",
            icon: <FaPlus />,
            action: createNewPath,
        },
        {
            name: "Buscador de Arquivos",
            icon: <FaSearch />,
            action: searchFile,
        }
    ];

    const getPathName = async () => {
        try {
            if (!pathId) {
                setPathName(PathsInFileManager.find(x => x.id == pathMain)?.name ?? "");
            } else {
                await GetSubPathNameAPI(pathId, company.id).then((resp: IResponseProps) => {
                    if (resp.statusCode == 200) {
                        setPathName(resp.data.pathName);
                    }
                }).finally(() => toggleLoading(false));
            }
        } catch (error: any) {
            toast.error(error);
            if (loading) {
                toggleLoading(false);
            }
        }
    }

    const getPaths = async (page?: number) => {
        try {
            toggleLoading(true);

            let text = searchTextPath == null || searchTextPath == "" ? "all" : searchTextPath;

            await GetSubPathsAPI(company.id, pathMain ?? "", pathId ?? "", page ?? 1, 7, text).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setPaths(resp.data.items);
                    setTotalPath(resp.data.total);
                }
            }).finally(() => {
                toggleLoading(false);
                if (pathsHasChanged) {
                    setPathsHasChanged(false);
                }
            });
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const getFiles = async (page?: number) => {
        try {
            toggleLoading(true);

            let path = PathsInFileManager.findIndex(x => x.id == pathMain);

            let text = searchText == null || searchText == "" ? "all" : searchText;

            await GetFilesAPI(company.id, pathId ?? "", path, page ?? 1, 7, text).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    setFiles(resp.data.items);
                    setCheckedState(new Array(resp.data.items.length).fill(false));
                    setTotal(resp.data.total);
                }
            }).finally(() => {
                toggleLoading(false);
                if (filesHasChanged) {
                    setFilesHasChanged(false);
                }
            });
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const deleteFilesMassive = async () => {
        try {
            let filesSelected = files.filter((x, index) => checkedState[index] == true).map(x => x.id);

            await DeleteMassiveFileAPI(filesSelected, company.id).then((resp: IResponseProps) => {
                if (resp.statusCode == 200) {
                    toast.success("Arquivos deletados com sucesso!");
                    getFiles();
                }
            }).finally(() => toggleLoading(false));
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    const downloadFilesMassive = async () => {
        try {
            toggleLoading(true);

            let filesSelected = files.filter((x, index) => checkedState[index] == true).map(x => x.id);

            await DownloadMassiveAPI(filesSelected, company.id).then((blobResp: any) => {
                var blob = new Blob([blobResp], {
                    type: 'application/octet-stream'
                });
                const fileURL = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = fileURL;
                link.setAttribute("download", "download.zip");
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
            }).finally(() => toggleLoading(false));
        } catch (error: any) {
            toast.error(error);
            toggleLoading(false);
        }
    }

    useEffect(() => {
        if (pathsHasChanged) {
            getPaths();
        }
    }, [pathsHasChanged]);

    useEffect(() => {
        if (checkedState.includes(true)) {
            setHasSomeCheck(true);
        } else {
            setHasSomeCheck(false);
        }
    }, [checkedState]);

    useEffect(() => {
        if (filesHasChanged) {
            getFiles();
        }
    }, [filesHasChanged]);

    useEffect(() => {
        if (deleteFiles) {
            deleteFilesMassive();
        }
    }, [deleteFiles]);

    useEffect(() => {
        getPathName();
        getPaths();
        getFiles();
    }, [pathId, pathMain]);

    return (
        <div className='card ml-2 mr-2'>
            {loading && <Loader />}
            <ModalDeleteMassive setResponse={setDeleteFiles} show={showModal} toggleShow={toggleShowModal} />
            <ModalSearchFileComponent
                companyId={company.id}
                handleClose={toggleShowSearchFileModal}
                pathMain={pathMain ?? ""}
                show={showSearchFileModal}
                toggleLoading={toggleLoading}
            />
            <CreatePathComponent
                show={showCreateModal}
                toggleLoading={toggleLoading}
                handleClose={toggleShowCreateModal}
                companyId={company.id}
                pathMain={pathMain ?? ""}
                path={pathId ?? ""}
                userEmail={userEmail}
                setPathsHasChanged={setPathsHasChanged}
            />
            <CardHeader
                pageName={pathName}
                hasPageBefore={true}
                linkPageBefore={`/fileManager/${company.id}`}
                pageBeforeName={"Gerenciador de Documentos"}
                buttons={!canNotDoNothing ? buttons : []}
            />
            <div className='card-body'>
                {!canNotDoNothing &&
                    <Fragment>
                        <UploadFileComponent
                            toggleLoading={toggleLoading}
                            companyId={company.id}
                            pathId={pathId ?? ""}
                            pathMain={pathMain ?? ""}
                            userEmail={userEmail}
                            setFilesHasChanged={setFilesHasChanged}
                        />
                        <div className='d-flex align-items-center justify-content-center'>
                            <div className='diviser'></div>
                        </div>
                    </Fragment>
                }
                <div className='d-flex justify-content-center'>
                    <FileTableComponent
                        checkedState={checkedState}
                        files={files}
                        getFiles={getFiles}
                        setCheckedState={setCheckedState}
                        setSearchText={setSearchText}
                        toggleLoading={toggleLoading}
                        total={total}
                        canNotDoNothing={canNotDoNothing}
                        companyId={company.id}
                    />
                    <PathTableComponent
                        paths={paths}
                        total={totalPath}
                        getPaths={getPaths}
                        setSearchTextPath={setSearchTextPath}
                        pathMain={pathMain ?? ""}
                        companyId={company.id}
                        toggleLoading={toggleLoading}
                        getFiles={getFiles}
                        setPathsHasChanged={setPathsHasChanged}
                        userEmail={userEmail}
                        canNotDoNothing={canNotDoNothing}
                    />
                </div>
                {hasSomeCheck &&
                    <div className='mt-2 d-flex align-items-center'>
                        <button className='btn btn-primary w-100 white-icon' onClick={downloadFilesMassive}>
                            <FaDownload size={16} className="me-2" />
                            Baixar Arquivos
                        </button>
                        {!canNotDoNothing &&
                            <button className='btn btn-secondary w-25 white-icon ms-2' onClick={toggleShowModal}>
                                <FaTrash size={16} />
                            </button>
                        }
                    </div>
                }
            </div>
        </div>
    )
}

export default PathManager;